// src/pages/Services/Login.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import {
  Container, Paper, Typography, TextField, Button, Alert, Link, Box, IconButton,
  FormControlLabel, Checkbox, Fade, InputAdornment, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip,
} from "@mui/material";
import PhoneAndroidOutlined from "@mui/icons-material/PhoneAndroidOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TranslateIcon from "@mui/icons-material/Translate";

import { useAuth } from "../../context/AuthContext";
import { updateLastActivity } from "../../utils/tokenUtils";
import AuthBackground from "../../components/AuthBackground";
import BrandLogo from "../../components/BrandLogo";
import { LANGUAGES, setLanguage } from "../../utils/language";

const WHATSAPP = "966569948945";

export default function Login() {
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const { t, i18n } = useTranslation();
  const tr = (key, fallback) => t(key, { defaultValue: fallback });

  const lang = (i18n.resolvedLanguage || i18n.language || "ar").slice(0, 2);
  const dir = i18n.dir ? i18n.dir(lang) : (lang === "ar" ? "rtl" : "ltr");

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("error");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const savedMobile = localStorage.getItem("rememberedMobile");
    if (savedMobile) {
      setMobile(savedMobile);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setMessage(null);
    setIsSubmitting(true);

    try {
      const response = await axios.post("/User/login", {
        mobile: mobile.trim(),
        password: password.trim(),
      });

      const { success, message: serverMessage, token, refreshToken, companyId, branchId, permissions = [] } = response.data;
      if (!success) throw new Error(serverMessage || tr("login.messages.loginFailed", "فشل تسجيل الدخول"));

      localStorage.setItem("token", token);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("branchId", branchId);

      // بدء جلسة جديدة: تسجيل وقت النشاط حتى لا يُعتبر المستخدم خاملاً عند أول طلب
      localStorage.removeItem("tokenTestStart");
      updateLastActivity();

      if (rememberMe) localStorage.setItem("rememberedMobile", mobile.trim());
      else localStorage.removeItem("rememberedMobile");

      const d = response.data;
      if (d.safeId) localStorage.setItem("safeId", d.safeId); else localStorage.removeItem("safeId");
      if (d.stockId) localStorage.setItem("stockId", d.stockId); else localStorage.removeItem("stockId");

      if (d.allowedSafeIds?.length > 0) localStorage.setItem("allowedSafeIds", JSON.stringify(d.allowedSafeIds));
      else localStorage.removeItem("allowedSafeIds");

      if (d.allowedStockIds?.length > 0) localStorage.setItem("allowedStockIds", JSON.stringify(d.allowedStockIds));
      else localStorage.removeItem("allowedStockIds");

      // القائمة الفارغة من السيرفر = كل الفروع (المالك/المدير) — لا نقيّده بفرعه الافتراضي
      if (d.allowedBranchIds?.length > 0) localStorage.setItem("allowedBranchIds", JSON.stringify(d.allowedBranchIds));
      else localStorage.removeItem("allowedBranchIds");

      if (d.userId) localStorage.setItem("userId", d.userId);
      if (d.userName) localStorage.setItem("userName", d.userName);
      if (d.companyName) localStorage.setItem("companyName", d.companyName);
      if (d.mobile) localStorage.setItem("mobile", d.mobile);

      setAuthData({ permissions });

      setSeverity("success");
      setMessage(serverMessage || tr("login.messages.loginSuccess", "تم تسجيل الدخول بنجاح، جاري التحويل..."));
      // من الخدمة المحلية (جهاز الكاشير) → نقطة البيع مباشرة، فلوحة التحكم تحتاج الإنترنت
      setTimeout(() => navigate(window.__PROFIT_AGENT__ ? "/pos" : "/dashboard"), 800);
    } catch (err) {
      setSeverity("error");
      setMessage(err.response?.data?.message || err.message || tr("login.messages.loginFailed", "فشل تسجيل الدخول"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px", bgcolor: "#f8fafc",
      "&:hover fieldset": { borderColor: "#4F46E5" },
      "&.Mui-focused": { bgcolor: "#fff" },
    },
  };

  return (
    <AuthBackground>
      <Fade in timeout={900}>
        <Container maxWidth="xs" dir={dir} sx={{ position: "relative", zIndex: 10 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3.5, sm: 4.5 }, pt: { xs: 4.5, sm: 5 },
              display: "flex", flexDirection: "column", alignItems: "center",
              borderRadius: "28px", width: "100%",
              background: "rgba(255,255,255,.98)",
              boxShadow: "0 40px 80px -20px rgba(0,0,0,.6)",
              position: "relative", overflow: "hidden",
              "& input:-webkit-autofill": { WebkitBoxShadow: "0 0 0 100px #f8fafc inset", WebkitTextFillColor: "#0f172a" },
            }}
          >
            <Box sx={{ height: 6, width: "100%", background: "linear-gradient(90deg, #4F46E5 0%, #10B981 100%)", position: "absolute", top: 0, left: 0 }} />

            {/* اختيار اللغة */}
            <Box sx={{ position: "absolute", top: 18, insetInlineEnd: 18, display: "flex", alignItems: "center", gap: .5 }}>
              <TranslateIcon sx={{ fontSize: 17, color: "#94a3b8" }} />
              <ToggleButtonGroup
                exclusive size="small" value={lang}
                onChange={(_, v) => v && v !== lang && setLanguage(v)}
                aria-label={tr("login.language", "اللغة")}
                sx={{
                  bgcolor: "#f1f5f9", borderRadius: "10px", p: .25,
                  "& .MuiToggleButton-root": {
                    border: "none !important", borderRadius: "8px !important", px: 1.1, py: .25, minWidth: 36,
                    fontWeight: 800, fontSize: 12.5, color: "#64748b", lineHeight: 1.6,
                    "&.Mui-selected": { bgcolor: "#fff", color: "#4F46E5", boxShadow: "0 1px 3px rgba(15,23,42,.12)" },
                    "&.Mui-selected:hover": { bgcolor: "#fff" },
                  },
                }}
              >
                {LANGUAGES.map(l => (
                  <ToggleButton key={l.code} value={l.code} aria-label={l.label} title={l.label}>{l.short}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* الشعار */}
            <Box sx={{
              mt: 1.5, mb: 2, borderRadius: "20px",
              filter: "drop-shadow(0 14px 22px rgba(79,70,229,.35))",
              transition: "transform .3s", "&:hover": { transform: "translateY(-2px) rotate(-2deg)" },
            }}>
              <BrandLogo size={68} />
            </Box>

            <Typography component="h1" sx={{ mb: .5, fontWeight: 900, fontSize: { xs: "1.45rem", sm: "1.6rem" }, color: "#0F172A", letterSpacing: "-.4px" }}>
              {tr("brand.name", "أرباح")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, fontWeight: 600, textAlign: "center" }}>
              {tr("login.welcome_subtitle", "مرحبا بك في برنامج أرباح")}
            </Typography>

            {message && (
              <Fade in>
                <Alert severity={severity} variant="filled" sx={{ width: "100%", mb: 2.5, borderRadius: "12px", fontWeight: 600 }}>
                  {message}
                </Alert>
              </Fade>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              <TextField
                fullWidth
                label={tr("login.mobile", "رقم الجوال")}
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                disabled={isSubmitting}
                required autoFocus
                autoComplete="username"
                inputProps={{ inputMode: "tel", dir: "ltr", style: { textAlign: dir === "rtl" ? "right" : "left" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneAndroidOutlined sx={{ color: "#94a3b8", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <TextField
                fullWidth
                label={tr("login.password", "كلمة المرور")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: "#94a3b8", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? tr("login.hidePassword", "إخفاء كلمة المرور") : tr("login.showPassword", "إظهار كلمة المرور")}>
                        <IconButton size="small" edge="end" onClick={() => setShowPassword(s => !s)} tabIndex={-1}>
                          {showPassword ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, px: .25 }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isSubmitting} size="small" />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>{tr("login.remember_me", "تذكرني")}</Typography>}
                />
                <Link
                  component={RouterLink} to="/forgot-password" variant="body2"
                  onClick={(e) => { if (isSubmitting) e.preventDefault(); }}
                  sx={{ fontWeight: 700, textDecoration: "none", color: "primary.main", "&:hover": { color: "primary.dark" } }}
                >
                  {tr("common.forgot_password", "نسيت كلمة المرور؟")}
                </Link>
              </Box>

              <Button
                type="submit" fullWidth variant="contained" size="large" disabled={isSubmitting}
                endIcon={isSubmitting
                  ? <CircularProgress size={20} color="inherit" />
                  : <LoginOutlined sx={{ transform: dir === "rtl" ? "rotate(180deg)" : "none" }} />}
                sx={{
                  py: 1.5, fontSize: "1rem", fontWeight: 800, mb: 2.5, borderRadius: "14px", textTransform: "none",
                  background: "linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)",
                  boxShadow: "0 10px 25px -6px rgba(79,70,229,.45)",
                  "& .MuiButton-endIcon": { marginInlineStart: 1, marginInlineEnd: -.5 },
                  "&.Mui-disabled": { color: "rgba(255,255,255,.9)", background: "linear-gradient(90deg, #6366F1 0%, #818CF8 100%)" },
                  "&:hover": { background: "linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)", boxShadow: "0 15px 30px -5px rgba(79,70,229,.5)" },
                }}
              >
                {isSubmitting ? tr("login.signingIn", "جاري تسجيل الدخول...") : tr("login.loginButton", "تسجيل الدخول")}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textAlign: "center" }}>
                {tr("login.noAccount", "ليس لديك حساب؟")}{" "}
                <Link component={RouterLink} to="/register"
                  sx={{ fontWeight: 800, textDecoration: "none", color: "secondary.main", "&:hover": { color: "secondary.dark", textDecoration: "underline" } }}>
                  {tr("login.createAccount", "إنشاء حساب جديد")}
                </Link>
              </Typography>
            </Box>
          </Paper>

          {/* التواصل */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Link
              href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer" underline="none"
              aria-label={tr("login.whatsappContact", "تواصل معنا عبر واتساب")}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 1, px: 1.75, py: .75, borderRadius: "999px",
                bgcolor: "rgba(255,255,255,.9)", border: "1px solid rgba(15,23,42,.1)", color: "#0f172a",
                fontWeight: 700, fontSize: 14, boxShadow: "0 6px 18px -10px rgba(0,0,0,.4)",
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              <WhatsAppIcon sx={{ color: "#16A34A", fontSize: 20 }} />
              <span>{tr("login.whatsappContact", "تواصل معنا عبر واتساب")}</span>
              <Box component="span" sx={{ direction: "ltr", fontWeight: 800, color: "#475569" }}>+{WHATSAPP}</Box>
            </Link>
          </Box>
        </Container>
      </Fade>
    </AuthBackground>
  );
}